/* eslint-disable max-lines */
// TODO: https://linear.app/clipboardhealth/issue/ACT-3976 -
// Once we remove hellosign code in cleanup, number of lines should be reduced

/* eslint-disable unicorn/prevent-abbreviations */
import { PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { addBrowserListener, openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useHelloSignEmbeddedFlow } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/useHelloSignEmbeddedFlow";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { DocumentStatus } from "../../../../../Accounts/Documents/types";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "../../../../../lib/analytics";
import { useDefinedWorker } from "../../../../../Worker/useDefinedWorker";
import { PageHeaderTopRow } from "../../../../components/PageHeaderTopRow";
import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_ERROR_PATH,
  DOCUMENT_SUBMISSION_IN_PROGRESS_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
} from "../../../paths";
import { DocumentSubmissionInProgressBottomSheet } from "../../../Submission/InProgressBottomSheet";
import { DocumentUploadFooter } from "../../Footer";
import { DocumentDetailsInstructions } from "../../Instructions";
import { DocumentDetailsMetadata } from "../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { DocumentDetailsStatusCard } from "../../StatusCard";
import { useDocumentDetailsActions } from "../../useDocumentDetailsActions";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { ESignWorkflowSessionStatus } from "../PandaDoc/constants";
import { PandaDocEmbeddedSessionLoadingBottomSheet } from "../PandaDoc/EmbeddedSessionLoadingBottomSheet";
import { getDocumentStatusOfESignWorkflowSession } from "../PandaDoc/getDocumentStatusOfESignWorkflowSession";
import { PandaDocEmbeddedSigning } from "../PandaDoc/PandaDocEmbeddedSigning";
import { usePandaDocSigningEnabled } from "../PandaDoc/usePandaDocSigningEnabled";
import { useSignableESignWorkflowSession } from "../PandaDoc/useSignableESignWorkflowSession";
import { useUpdateESignWorkflowSession } from "../PandaDoc/useUpdateESignWorkflowSession";
import { useHelloSignFallbackUrl } from "./useHelloSignFallbackUrl";

export function DocumentDetailsSignWithHelloSignPage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const pandaDocModalState = useModalState();
  const submittingDocumentModalState = useModalState();
  const loadingPandaDocModalState = useModalState();
  const scrollRef = useRef<HTMLDivElement>(null);
  const pandaDocSignCtaClickedAtRef = useRef<number>();
  const history = useHistory();
  const helloSignIframeContainerRef = useRef<HTMLDivElement>(null);
  const helloSignFormModalState = useModalState();
  const queryClient = useQueryClient();
  const { showInfoToast } = useToast();
  const worker = useDefinedWorker();
  const workerId = worker.userId;

  const { status, requirementId, requirement } = workerRequirement;
  const { name } = requirement?.attributes ?? {};

  const { deleteDocument } = useDocumentDetailsActions();
  const shouldUseHelloSignFallbackUrl = useHelloSignFallbackUrl();

  const isPandaDocSigningEnabled = usePandaDocSigningEnabled(requirementId);
  const {
    mutate: loadSignableESignWorkflowSession,
    data: signableESignWorkflowSession,
    isLoading: isLoadingSignableESignWorkflowSession,
  } = useSignableESignWorkflowSession({
    requirementId,
    onError: useCallback(
      (error: unknown) => {
        pandaDocModalState.closeModal();
        logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
          error,
          metadata: { requirementId },
        });
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
            requirementId,
          })
        );
      },
      [pandaDocModalState, history, requirementId]
    ),
  });
  const { mutateAsync: updateESignWorkflowSession } = useUpdateESignWorkflowSession();

  const onPandaDocCompleted = useCallback(async () => {
    pandaDocModalState.closeModal();
    submittingDocumentModalState.openModal();
    let modalPath = DOCUMENT_SUBMISSION_IN_REVIEW_PATH;
    try {
      // In runtime we will always have a signableESignWorkflowSession
      if (!isDefined(signableESignWorkflowSession)) {
        throw new Error("signableESignWorkflowSession is not defined");
      }

      const updatedSessionResponse = await updateESignWorkflowSession({
        requirementId,
        sessionId: signableESignWorkflowSession.id,
        attributesToUpdate: { status: ESignWorkflowSessionStatus.SIGNED },
      });

      const documentStatus = updatedSessionResponse
        ? await getDocumentStatusOfESignWorkflowSession({
            data: updatedSessionResponse.data,
            included: updatedSessionResponse.included,
          })
        : await getDocumentStatusOfESignWorkflowSession({
            requirementId,
            workerId,
          });

      modalPath =
        documentStatus === DocumentStatus.APPROVED ? DOCUMENT_SUBMISSION_APPROVED_PATH : modalPath;
    } catch (error: unknown) {
      logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
        error,
        metadata: { requirementId },
      });
      void invalidateWorkerRequirements(queryClient);
      submittingDocumentModalState.closeModal();
      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
          requirementId,
        })
      );
      return;
    }

    void invalidateWorkerRequirements(queryClient);

    submittingDocumentModalState.closeModal();

    logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_SIGNED, {
      requirementId,
      signType: "pandaDoc",
    });
    history.replace(
      generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
        requirementId,
      })
    );
  }, [
    history,
    queryClient,
    workerId,
    submittingDocumentModalState,
    requirementId,
    pandaDocModalState,
    signableESignWorkflowSession,
    updateESignWorkflowSession,
  ]);

  const { isCreatingHelloSignSession, isSubmittingHelloSignDocument, startHelloSignEmbeddedFlow } =
    useHelloSignEmbeddedFlow({
      requirementId,
      onSuccess: (isDocumentApproved) => {
        const modalPath = isDocumentApproved
          ? DOCUMENT_SUBMISSION_APPROVED_PATH
          : DOCUMENT_SUBMISSION_IN_REVIEW_PATH;

        void invalidateWorkerRequirements(queryClient);
        helloSignFormModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
            requirementId,
          })
        );
      },
      onError: () => {
        helloSignFormModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
            requirementId,
          })
        );
      },
      helloSignIframeContainerRef,
      onHelloSignClientCancel: () => {
        helloSignFormModalState.closeModal();
      },
    });

  useEffect(() => {
    if (isSubmittingHelloSignDocument) {
      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_IN_PROGRESS_PATH}`, {
          requirementId,
        })
      );
    }
  }, [history, isSubmittingHelloSignDocument, requirementId]);

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={name ?? ""}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsMetadata />
        <DocumentDetailsStatusCard />
        <DocumentDetailsInstructions />
        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      <DocumentUploadFooter
        status={status}
        isLoading={isCreatingHelloSignSession || isLoadingSignableESignWorkflowSession}
        onSignButtonClick={async () => {
          if (isPandaDocSigningEnabled) {
            pandaDocModalState.openModal();
            loadingPandaDocModalState.openModal();
            pandaDocSignCtaClickedAtRef.current = Date.now();
            void loadSignableESignWorkflowSession({});
            logEvent(APP_V2_USER_EVENTS.PANDADOC_SIGN_CTA_CLICKED, {
              requirementId,
              signType: "pandaDoc",
            });
            return;
          }

          if (
            isDefined(requirement?.attributes.helloSignConfig?.fallbackStaticUrl) &&
            (shouldUseHelloSignFallbackUrl ||
              !requirement.attributes.helloSignConfig.isEmbeddedFlowEnabled)
          ) {
            const browserFinishedListener = await addBrowserListener(
              "browserFinished",
              async () => {
                showInfoToast(
                  "If you have signed this document, please check back here later for an update"
                );
                await browserFinishedListener.remove();
              }
            );
            await openInBrowser({ url: requirement.attributes.helloSignConfig.fallbackStaticUrl });
          } else {
            helloSignFormModalState.openModal();
            void startHelloSignEmbeddedFlow();
          }
        }}
        onDeleteButtonClick={deleteDocument}
      />

      {/* Using skipTopSafeAreaPadding because we are using a custom header */}
      <FullScreenDialog skipTopSafeAreaPadding modalState={pandaDocModalState}>
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow
            title={name}
            onClickBack={() => {
              pandaDocModalState.closeModal();
            }}
          />
        </PageHeaderWrapper>
        <Box sx={{ height: "100%", overflow: "auto" }}>
          {isDefined(signableESignWorkflowSession?.attributes.esignProviderData?.sessionId) && (
            <PandaDocEmbeddedSigning
              pandaDocSessionId={
                signableESignWorkflowSession?.attributes.esignProviderData.sessionId
              }
              onDocumentLoaded={() => {
                loadingPandaDocModalState.closeModal();
                const timeToLoadInMilliseconds = pandaDocSignCtaClickedAtRef.current
                  ? Date.now() - pandaDocSignCtaClickedAtRef.current
                  : undefined;
                logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_LOADED, {
                  requirementId,
                  signType: "pandaDoc",
                  timeToLoadInMilliseconds,
                });
                void updateESignWorkflowSession({
                  requirementId,
                  sessionId: signableESignWorkflowSession.id,
                  attributesToUpdate: { status: ESignWorkflowSessionStatus.START },
                });
              }}
              onError={() => {
                pandaDocModalState.closeModal();
                logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
                  error: new Error("PandaDoc Embedded Signing SDK Error"),
                  metadata: { requirementId },
                });
                history.replace(
                  generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
                    requirementId,
                  })
                );
              }}
              onComplete={onPandaDocCompleted}
            />
          )}
        </Box>
        <PandaDocEmbeddedSessionLoadingBottomSheet modalState={loadingPandaDocModalState} />
      </FullScreenDialog>
      <FullScreenDialog modalState={helloSignFormModalState}>
        <Box ref={helloSignIframeContainerRef} sx={{ height: "100%" }} />
      </FullScreenDialog>

      <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
    </PageWrapper>
  );
}
/* eslint-enable max-lines */
/* eslint-enable unicorn/prevent-abbreviations */
